'use client'

import React from 'react'
import Calendar from 'react-calendar'

export interface CustomCalendarProps {
  selected: Date | null
  onChange: (date: Date) => void
  maxDate?: Date
  minDate?: Date
  className?: string
  locale?: string
  onReady?: () => void

  isDateDisabled?: (date: Date) => boolean
  /** Return a class name for specific dates, for example "cal-holiday" */
  getTileClassName?: (date: Date) => string | undefined

  /** When true, show X overlay on past dates, used in booking flow */
  showPastCross?: boolean

  /** When true, hide the jump-to-prev/next-month double arrows */
  hideDoubleArrows?: boolean
}

type ValuePiece = Date | null
type Range = [ValuePiece, ValuePiece]
type CalendarValue = ValuePiece | Range

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  selected,
  onChange,
  maxDate,
  minDate,
  className,
  locale = 'en-IN',
  onReady,
  isDateDisabled,
  getTileClassName,
  showPastCross = false,
  hideDoubleArrows = false,
}) => {
  const [value, setValue] = React.useState<Date | null>(selected)

  React.useEffect(() => {
    setValue(selected)
  }, [selected])

  const handleChange = (next: CalendarValue) => {
    const picked = Array.isArray(next) ? next[0] : next

    if (picked) {
      const d = new Date(picked)
      d.setHours(0, 0, 0, 0)
      setValue(d)
      onChange(d)
    }
  }

  const todayKey = React.useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  }, [])

  React.useEffect(() => {
    const id = requestAnimationFrame(() => {
      onReady?.()
    })

    return () => cancelAnimationFrame(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const normalize = (date: Date) => {
    const d = new Date(date)
    d.setHours(0, 0, 0, 0)
    return d
  }

  return (
    <div className={`calendar-container ${className ?? ''}`}>
      <Calendar
        value={value ?? undefined}
        onChange={handleChange}
        defaultView="month"
        minDetail="century"
        maxDetail="month"
        selectRange={false}
        showNeighboringMonth={false}
        prev2Label={hideDoubleArrows ? null : '«'}
        next2Label={hideDoubleArrows ? null : '»'}
        prevLabel="‹"
        nextLabel="›"
        locale={locale}
        maxDate={maxDate}
        minDate={minDate}
        tileDisabled={
          isDateDisabled
            ? ({ date, view }) =>
                view === 'month' && !!isDateDisabled(normalize(date))
            : undefined
        }
        tileClassName={({ date, view }) => {
          if (view !== 'month') return undefined

          const d = normalize(date)
          const classes: string[] = []

          if (d.getTime() === todayKey) classes.push('cal-today')

          if (showPastCross && d.getTime() < todayKey) {
            classes.push('cal-past')
          }

          const extra = getTileClassName?.(d)

          if (extra) classes.push(extra)

          return classes.length ? classes.join(' ') : undefined
        }}
      />

      <style jsx>{`
        .calendar-container {
          --calendar-day-row-height: 46px;
          --calendar-day-circle-size: 36px;

          width: 100%;
          max-width: none;
          margin: 0;
          border-radius: 16px;
          background: #ffffff;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.07);
          overflow: hidden;
          padding: 0.875rem;
        }

        @media (min-width: 480px) {
          .calendar-container {
            --calendar-day-row-height: 52px;
            --calendar-day-circle-size: 40px;
            padding: 1rem;
          }
        }

        @media (min-width: 640px) {
          .calendar-container {
            --calendar-day-row-height: 58px;
            --calendar-day-circle-size: 44px;
            padding: 1.125rem;
          }
        }

        @media (min-width: 1024px) {
          .calendar-container {
            --calendar-day-row-height: 64px;
            --calendar-day-circle-size: 48px;
            padding: 1.25rem;
          }
        }
      `}</style>

      <style jsx global>{`
        /* ══════════════════════════════════════════
           Base calendar
           ══════════════════════════════════════════ */
        .calendar-container .react-calendar {
          width: 100%;
          border: none;
          background: transparent;
          font-family: inherit;
        }

        .calendar-container .react-calendar,
        .calendar-container .react-calendar *,
        .calendar-container .react-calendar *::before,
        .calendar-container .react-calendar *::after {
          box-sizing: border-box;
        }

        /* ══════════════════════════════════════════
           Navigation
           ══════════════════════════════════════════ */
        .calendar-container .react-calendar__navigation {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          margin-bottom: 1rem;
          min-height: 40px;
        }

        .calendar-container
          .react-calendar__navigation
          .react-calendar__navigation__prev2-button,
        .calendar-container
          .react-calendar__navigation
          .react-calendar__navigation__next2-button {
          display: none;
        }

        .calendar-container .react-calendar__navigation button {
          min-width: 34px;
          min-height: 34px;
          border: none;
          border-radius: 999px;
          background: transparent;
          color: #2563eb;
          font-size: 1.15rem;
          font-weight: 600;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition:
            background-color 0.2s ease,
            color 0.2s ease,
            opacity 0.2s ease;
        }

        .calendar-container
          .react-calendar__navigation
          button:hover:not(:disabled) {
          background: #eff6ff;
          color: #1d4ed8;
        }

        .calendar-container .react-calendar__navigation button:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }

        .calendar-container .react-calendar__navigation__label {
          flex: 1;
          color: #2563eb;
          font-size: 1rem;
          font-weight: 700;
          text-align: center;
        }

        @media (min-width: 640px) {
          .calendar-container .react-calendar__navigation__label {
            font-size: 1.15rem;
          }
        }

        .calendar-container .react-calendar__navigation button:focus-visible,
        .calendar-container .react-calendar__tile:focus-visible {
          outline: 2px solid #2563eb;
          outline-offset: 2px;
        }

        /* ══════════════════════════════════════════
           Weekday headers
           IMPORTANT: React Calendar handles date offsets internally.
           Do not force the day tiles into CSS grid.
           ══════════════════════════════════════════ */
        .calendar-container .react-calendar__month-view__weekdays {
          display: flex !important;
          margin-bottom: 0.75rem;
          color: #6b7280;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }

        @media (min-width: 640px) {
          .calendar-container .react-calendar__month-view__weekdays {
            font-size: 0.78rem;
            margin-bottom: 0.55rem;
          }
        }

        .calendar-container .react-calendar__month-view__weekdays__weekday {
          flex: 0 0 14.285714%;
          max-width: 14.285714%;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 0;
        }

        .calendar-container
          .react-calendar__month-view__weekdays__weekday
          abbr {
          text-decoration: none;
        }

        /* ══════════════════════════════════════════
           Month days
           Keep flex layout so May 29, 2026 correctly lands on Friday.
           ══════════════════════════════════════════ */
        .calendar-container .react-calendar__month-view__days {
          display: flex !important;
          flex-wrap: wrap !important;
          align-items: center;
          row-gap: 0.45rem;
        }

        @media (min-width: 640px) {
          .calendar-container .react-calendar__month-view__days {
            row-gap: 0.45rem;
          }
        }

        .calendar-container
          .react-calendar__month-view__days
          .react-calendar__tile {
          flex: 0 0 14.285714% !important;
          max-width: 14.285714% !important;
          height: var(--calendar-day-row-height);
          padding: 0;
          margin: 0;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          border-radius: 0;
          background: transparent;
          color: #111827;
          font-size: 0.82rem;
          font-weight: 500;
          line-height: 1;
          text-align: center;
          cursor: pointer;
          transition:
            color 0.2s ease,
            opacity 0.2s ease;
        }

        @media (min-width: 640px) {
          .calendar-container
            .react-calendar__month-view__days
            .react-calendar__tile {
            font-size: 0.95rem;
          }
        }

        .calendar-container
          .react-calendar__month-view__days
          .react-calendar__tile
          abbr {
          width: var(--calendar-day-circle-size);
          height: var(--calendar-day-circle-size);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid transparent;
          border-radius: 999px;
          transition:
            background-color 0.2s ease,
            border-color 0.2s ease,
            color 0.2s ease,
            box-shadow 0.2s ease;
        }

        .calendar-container
          .react-calendar__month-view__days
          .react-calendar__tile:hover:not(:disabled)
          abbr {
          background-color: #eff6ff;
          border-color: #bfdbfe;
        }

        /* Remove library default now styling in month view */
        .calendar-container
          .react-calendar__month-view__days
          .react-calendar__tile--now {
          background: transparent;
          color: inherit;
        }

        /* Selected date */
        .calendar-container
          .react-calendar__month-view__days
          .react-calendar__tile--active
          abbr {
          background-color: #2563eb !important;
          color: #ffffff !important;
          border-color: #1d4ed8 !important;
          font-weight: 700;
          box-shadow: 0 8px 20px rgba(37, 99, 235, 0.32);
        }

        /* Today */
        .calendar-container .cal-today abbr {
          background-color: #fef9c3 !important;
          border-color: #facc15 !important;
          color: #854d0e !important;
          font-weight: 700;
        }

        .calendar-container .cal-today.react-calendar__tile--active abbr {
          background-color: #2563eb !important;
          color: #ffffff !important;
          border-color: #1d4ed8 !important;
        }

        /* Disabled tiles */
        .calendar-container .react-calendar__tile:disabled {
          cursor: not-allowed;
          opacity: 0.42;
        }

        /* Past dates: soft X overlay */
        .calendar-container .cal-past {
          color: #9ca3af !important;
          opacity: 0.48;
        }

        .calendar-container .cal-past::before,
        .calendar-container .cal-past::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 20px;
          height: 1.5px;
          background-color: #cbd5e1;
          border-radius: 999px;
          pointer-events: none;
          z-index: 2;
        }

        .calendar-container .cal-past::before {
          transform: translate(-50%, -50%) rotate(45deg);
        }

        .calendar-container .cal-past::after {
          transform: translate(-50%, -50%) rotate(-45deg);
        }

        .calendar-container .react-calendar__tile:disabled.cal-past {
          opacity: 0.38;
        }

        /* Future beyond booking window */
        .calendar-container .cal-future-locked {
          color: #cbd5e1 !important;
          opacity: 0.55;
        }

        .calendar-container .cal-future-locked::after {
          content: '';
          position: absolute;
          right: calc(50% - 14px);
          bottom: 5px;
          width: 5px;
          height: 5px;
          border-radius: 999px;
          background: #cbd5e1;
          opacity: 0.75;
          pointer-events: none;
        }

        .calendar-container .react-calendar__tile:disabled.cal-future-locked {
          opacity: 0.42;
        }

        /* Holidays */
        .calendar-container .cal-holiday abbr {
          background-color: #fee2e2 !important;
          color: #b91c1c !important;
          border-color: #fca5a5 !important;
          font-weight: 700;
        }

        .calendar-container .cal-holiday.react-calendar__tile--active abbr {
          background-color: #fecaca !important;
          color: #7f1d1d !important;
          border-color: #ef4444 !important;
        }

        /* ══════════════════════════════════════════
           Year / Decade / Century views
           ══════════════════════════════════════════ */
        .calendar-container .react-calendar__year-view .react-calendar__tile,
        .calendar-container .react-calendar__decade-view .react-calendar__tile,
        .calendar-container
          .react-calendar__century-view
          .react-calendar__tile {
          border: 2px solid transparent;
          border-radius: 10px;
          background: transparent;
          color: #111827;
          font-size: 0.85rem;
          font-weight: 500;
          padding: 0.75rem 0.4rem;
          transition:
            background-color 0.2s ease,
            border-color 0.2s ease,
            color 0.2s ease;
        }

        .calendar-container
          .react-calendar__year-view
          .react-calendar__tile:hover:not(:disabled),
        .calendar-container
          .react-calendar__decade-view
          .react-calendar__tile:hover:not(:disabled),
        .calendar-container
          .react-calendar__century-view
          .react-calendar__tile:hover:not(:disabled) {
          background-color: #eff6ff;
          border-color: #bfdbfe;
        }

        .calendar-container
          .react-calendar__year-view
          .react-calendar__tile--active,
        .calendar-container
          .react-calendar__decade-view
          .react-calendar__tile--active,
        .calendar-container
          .react-calendar__century-view
          .react-calendar__tile--active,
        .calendar-container
          .react-calendar__year-view
          .react-calendar__tile--hasActive,
        .calendar-container
          .react-calendar__decade-view
          .react-calendar__tile--hasActive,
        .calendar-container
          .react-calendar__century-view
          .react-calendar__tile--hasActive {
          background-color: #dbeafe !important;
          color: #1d4ed8 !important;
          border-color: #93c5fd !important;
          font-weight: 700;
        }

        .calendar-container
          .react-calendar__year-view
          .react-calendar__tile--now,
        .calendar-container
          .react-calendar__decade-view
          .react-calendar__tile--now,
        .calendar-container
          .react-calendar__century-view
          .react-calendar__tile--now {
          background-color: #fef9c3;
          border-color: #facc15;
          color: #854d0e;
          font-weight: 700;
        }
      `}</style>
    </div>
  )
}

export default CustomCalendar
